<template>
    <v-app id="inspire" ref="app">
        <v-alert v-if="showAnnouncementBanner" ref="announcementBanner" type="error" color="red" class="announcement-banner white--text">
            Platform Maintenance Notice: Nuvolos will undergo an urgent security update today, July 13, 2026, starting at 6:00 PM CEST, estimated completion:
            7:30 PM CEST. Applications will be temporarily restarted during this window. Please ensure your progress is saved.
        </v-alert>

        <AppBar v-if="showAppChrome" />

        <NavigationDrawer v-if="showGlobalDrawer" />

        <router-view name="permanent-sidebar"></router-view>

        <v-main :style="vMainStyle" class="intercom-padding">
            <transition name="fadeonly" mode="out-in">
                <router-view></router-view>
            </transition>
        </v-main>
        <v-snackbar text :value="$store.state['snackBar']" :timeout="-1" elevation="1">
            <div class="d-flex align-center">
                <v-icon class="mr-3" :color="snackBarIcon === 'check_circle' ? 'success' : snackBarIcon === 'error' ? 'error' : 'info'">
                    {{ snackBarIcon }}
                </v-icon>
                <div v-html="snackBarText"></div>
                <v-spacer />
                <v-btn :text="snackBarIcon !== 'cookie'" class="ml-5" @click="$store.dispatch('hideSnackBar')" color="secondary">
                    {{ snackBarLabel || 'Close' }}
                </v-btn>
            </div>
        </v-snackbar>
        <GlobalDialog />
        <v-snackbar v-model="showUpdatePwa" color="primary" :vertical="true" :timeout="forcePWAUpdate ? 0 : 10000">
            <span v-if="!forcePWAUpdate">A new version of Nuvolos is available. Update?</span>
            <span v-else>Your Nuvolos version requires updating!</span>
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="update">Update</v-btn>
            </template>
        </v-snackbar>
        <v-snackbar color="error" text v-model="intercomFailed" :timeout="-1" right>
            Failed to load the support chat interface, please reach out to us at
            <a href="mailto:support@nuvolos.cloud">support@nuvolos.cloud</a>
            if you need assistance.
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="intercomFailed = false">Close</v-btn>
            </template>
        </v-snackbar>
    </v-app>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { parseURL } from './utils'
import pwa from '@/mixins/pwa'
import { enumsData } from '@/mixins/enums'
import { displayCookiePopup } from '@/cookiePopup'
import { DateTime } from 'luxon'
import { analyticsMixin } from '@/mixins/analytics'
import {
    connect as connectPushSocket,
    disconnect as disconnectPushSocket,
    on as onPushEvent,
    onConnected as onPushConnected,
    onStatus as onPushStatus,
    onPushFallback,
    PushEventKind
} from '@/push'
import { loadIntercom } from '@/utils/intercom'
import { hideFrigadeAssistant, isFrigadeAssistantOpen, restoreFrigadeAssistant } from '@/utils/frigade'

const AppBar = () => import('@/components/AppBar.vue')
const GlobalDialog = () => import('@/components/GlobalDialog')
const NavigationDrawer = () => import('@/components/NavigationDrawer.vue')

export default {
    name: 'app',
    mixins: [enumsData, pwa, analyticsMixin],
    components: {
        AppBar,
        GlobalDialog,
        NavigationDrawer
    },
    data() {
        return {
            statusUpdater: null,
            // pushFallbackUnsub / taskFallbackInterval live on the instance
            // (like _unsubPushConnected), not in data — vue/no-reserved-keys.
            showUsePwaTrigger: true,
            showUpdatePwaTrigger: false,
            forcePWAUpdate: false,
            intercomFailed: false,
            frigadeFailed: false,
            frigadeDismissListenerRegistered: false,
            frigadeContextKey: null,
            showAnnouncementBanner: false,
            announcementBannerObserver: null
        }
    },
    methods: {
        beforeLeave(element) {
            this.prevHeight = getComputedStyle(element).height
        },
        enter(element) {
            const { height } = getComputedStyle(element)

            element.style.height = this.prevHeight

            setTimeout(() => {
                element.style.height = height
            })
        },
        afterEnter(element) {
            element.style.height = 'auto'
        },
        dismiss() {
            const date = new Date()
            date.setDate(date.getDate() + 3)
            this.$store.commit('setUserMetadata', { show_install_pwa: date })
            window.deferredPrompt = null
        },
        dismissUsePwa() {
            localStorage.setItem('use-pwa-dismissed', new Date().toISOString())
            this.showUsePwaTrigger = false
        },
        async update() {
            this.showUpdatePwaTrigger = false
            if (this.$workbox) {
                await this.$workbox.messageSW({ type: 'SKIP_WAITING' })
            } else {
                location.reload()
            }
        },
        withIntercomLauncherHidden(payload = {}) {
            return {
                ...payload,
                hide_default_launcher: true
            }
        },
        registerAnnouncementBanner() {
            this.$nextTick(() => {
                const el = this.$refs.announcementBanner?.$el
                if (!el) return
                const updateHeight = () => {
                    this.$vuetify.application.register('announcement-banner', 'bar', el.offsetHeight)
                }
                updateHeight()
                this.announcementBannerObserver = new ResizeObserver(updateHeight)
                this.announcementBannerObserver.observe(el)
            })
        },
        unregisterAnnouncementBanner() {
            this.announcementBannerObserver?.disconnect()
            this.announcementBannerObserver = null
            this.$vuetify.application.unregister('announcement-banner', 'bar')
        },
        intercomBoot(payload = {}) {
            const intercom = loadIntercom(this.$appConfig?.VUE_APP_INTERCOM_ID)
            window.intercomSettings = this.withIntercomLauncherHidden(payload)
            intercom?.('boot', window.intercomSettings)
        },
        intercomUpdate(payload = {}) {
            window.Intercom?.('update', this.withIntercomLauncherHidden(payload))
        },
        bootIntercom(user = this.userInfo) {
            window.Intercom?.('shutdown')

            if (!this.$appConfig?.VUE_APP_INTERCOM_ID) {
                return
            }

            if (!this.$route.params.aid && user?.hmac) {
                this.intercomBoot({
                    app_id: this.$appConfig.VUE_APP_INTERCOM_ID,
                    email: user.email,
                    user_id: user.uid,
                    user_hash: user.hmac,
                    name: user.name || user.given_name + ' ' + user.family_name,
                    user_type: this.isOrgManager ? 'manager' : this.isOrgFaculty ? 'faculty' : 'member',
                    is_trial_user: user.is_trial_user,
                    ucid: user.ucid
                })
                if (this.isAnalyticsEnabled) {
                    this.intercomUpdate({
                        has_analytics_data: true
                    })
                } else {
                    this.intercomUpdate({
                        has_analytics_data: false
                    })
                    window['ga-disable-G-1DK5JS9X22'] = true
                }
                return
            }

            this.intercomBoot({
                app_id: this.$appConfig.VUE_APP_INTERCOM_ID
            })
        },
        bootFrigade(user) {
            if (window.FrigadeAssistant?.failedToLoad) {
                this.frigadeFailed = true
                console.warn('Frigade Assistant failed to load. Skipping initialization.')
                return
            }

            if (!window.FrigadeAssistant?.loaded) {
                console.info('Frigade Assistant script not loaded yet, queuing initialization call.')
            }

            const permissions = [
                {
                    permission: 'org_manager',
                    description: 'Can manage organization, invite/revoke users, monitor resources',
                    granted: this.isOrgManager
                },
                {
                    permission: 'org_faculty',
                    description: 'Can create spaces, invite users, distribute content',
                    granted: this.isOrgFaculty
                },
                {
                    permission: 'org_member',
                    description: 'Can view public spaces, access invited content',
                    granted: this.isOrgMember
                },
                {
                    permission: 'space_admin',
                    description: 'Full admin in a space: manage users, files, instances',
                    granted: this.isSpaceAdmin
                },
                {
                    permission: 'instance_editor',
                    description: 'Can modify instance contents, run apps, upload/download files',
                    granted: this.isInstanceEditor
                },
                {
                    permission: 'instance_viewer',
                    description: 'Can view instance contents, query data',
                    granted: this.isInstanceViewer
                },
                {
                    permission: 'instance_observer',
                    description: 'Can view README, request viewer role',
                    granted: this.isInstanceObserver
                }
            ]

            const frigadeContextKey = JSON.stringify({
                userId: user.uid.toString(),
                firstName: user.given_name,
                lastName: user.family_name,
                email: user.email,
                permissions: permissions.map(({ permission, granted }) => ({ permission, granted }))
            })
            if (frigadeContextKey === this.frigadeContextKey) return

            const shouldRestoreAssistant = isFrigadeAssistantOpen()
            const previousFrigadeShow = window.FrigadeAssistant?.show

            window.FrigadeAssistant({
                apiKey: 'fr_public_key_cJ6ppPYozzOCCp9BH6L10vbyt6cRX011F7MwZ7FHNkesgncXkVByfccplE6THcQw',
                apiUrl: 'https://eu.frigade.ai',
                assistantId: 'assistant_ilpqAUVztVxG',
                userId: user.uid.toString(),
                showWidget: false,
                userProperties: {
                    firstName: user.given_name,
                    lastName: user.family_name,
                    // imageUrl: user.picture,
                    email: user.email,
                    permissions
                },
                tools: {
                    handOffToHumanSupport: {
                        name: 'Hand off to human support',
                        description: `Escalates the conversation to human support.
        Only do this if the user explicitly asks for human help or support help or if they become confrontational.
        Always confirm with the user before handing over to support.
        Include a brief summary written from the user's perspective (what they need help with) to pass to the support agent.`,
                        inputSchema: {
                            type: 'object',
                            properties: {
                                conversationSummary: {
                                    type: 'string',
                                    description:
                                        "A brief summary from the user's perspective: what they're trying to accomplish and what help they need. This will be pre-filled in the new support message."
                                }
                            },
                            required: ['conversationSummary']
                        },
                        execute: async inputData => {
                            const summary = inputData.conversationSummary || ''

                            // Explicit handoff path: open support chat only when the assistant requests it.
                            this.bootIntercom(this.userInfo)

                            const intercom = window.Intercom
                            if (!intercom) {
                                this.intercomFailed = true
                                return 'Support chat is currently unavailable. Please email support@nuvolos.cloud.'
                            }

                            this.intercomUpdate()
                            intercom('show')
                            intercom('showNewMessage', summary)

                            return 'Opening Intercom to connect you with our support team.'
                        }
                    }
                }
            })
            this.frigadeContextKey = frigadeContextKey

            if (!this.frigadeDismissListenerRegistered && typeof window.FrigadeAssistant?.on === 'function') {
                this.frigadeDismissListenerRegistered = true
                window.FrigadeAssistant.on('dismiss', payload => {
                    console.log('Assistant dismissed', payload)
                    hideFrigadeAssistant()
                })
            }

            if (shouldRestoreAssistant) {
                restoreFrigadeAssistant(previousFrigadeShow)
            }
        },
        tryBootFrigade(user = this.userInfo) {
            if (!user || !this.areFrigadeRolesReady) {
                return
            }
            this.bootFrigade(user)
        },
        stopStatusPoll() {
            clearInterval(this.statusUpdater)
            this.statusUpdater = null
            clearInterval(this._taskFallbackInterval)
            this._taskFallbackInterval = null
        },
        startStatusPollFallback() {
            this.stopStatusPoll()
            // Immediate resync so UI is not stuck waiting for the first tick.
            if (this.$auth.isAuthenticated) {
                this.$store.dispatch('appStore/updateDeploymentStatus')
                this.$store.dispatch('userStore/fetchRunningUserTasks')
            }
            // Old standing cadence (10s app_status, 120s running-tasks badge).
            this.statusUpdater = setInterval(() => {
                if (this.$auth.isAuthenticated) {
                    this.$store.dispatch('appStore/updateDeploymentStatus')
                }
            }, 10000)
            this._taskFallbackInterval = setInterval(() => {
                if (this.$auth.isAuthenticated) {
                    this.$store.dispatch('userStore/fetchRunningUserTasks')
                }
            }, 120000)
        }
    },
    created() {
        // Push is the primary wake-up for app/deployment status. Full snapshot
        // still comes from GET /proxy/app_status (event payload is aid+status only).
        // asOfTime de-dupes overlapping fetches.
        onPushEvent(PushEventKind.DEPLOYMENT_STATUS_CHANGED, () => {
            this.$store.dispatch('appStore/updateDeploymentStatus')
        })
        // Runtime app failures (OOMKilled, CrashLoopBackOff, …) from go-informer pods.
        this._unsubAppAlert = onPushEvent(PushEventKind.APP_ALERT, data => {
            if (!this.$auth.isAuthenticated || !data) return
            const aid = data.aid
            const reason = data.reason || 'Error'
            const apps = this.$store.state.appStore?.userApps || []
            const deploys = this.$store.state.appStore?.deploymentStatus || []
            const fromDeploy = deploys.find(d => String(d.aid) === String(aid))
            const fromApp = apps.find(a => String(a.aid) === String(aid))
            const appName = (fromDeploy && fromDeploy.name) || (fromApp && (fromApp.long_id || fromApp.name)) || `app ${aid}`
            const container = data.containerName ? ` (${data.containerName})` : ''
            const detail = data.message ? `: ${data.message}` : ''
            // Keep message short for snackbar; full k8s message can be huge.
            const detailShort = detail.length > 180 ? detail.slice(0, 177) + '…' : detail
            const friendly = {
                OOMKilled: 'ran out of memory (OOMKilled)',
                CrashLoopBackOff: 'is crash-looping',
                ImagePullBackOff: 'cannot pull its image',
                ErrImagePull: 'failed to pull its image',
                Evicted: 'was evicted from the node',
                Failed: 'failed',
                CreateContainerConfigError: 'has a container config error',
                CreateContainerError: 'failed to create its container',
                InvalidImageName: 'has an invalid image name',
                RunContainerError: 'failed to start its container'
            }
            const what = friendly[reason] || `reported ${reason}`
            this.$store.dispatch('showSnackBar', {
                snackBarText: `Application <strong>${appName}</strong>${container} ${what}${detailShort}`,
                snackBarIcon: 'error',
                snackBarTimeout: 15000
            })
            // Status may have changed (replicas / crash) — refresh app list.
            this.$store.dispatch('appStore/updateDeploymentStatus')
        })
        this._unsubAppAutoStopped = onPushEvent(PushEventKind.APP_AUTO_STOPPED, data => {
            if (!this.$auth.isAuthenticated || !data) return
            const aid = data.aid
            const reason = data.reason || 'Error'
            const apps = this.$store.state.appStore?.userApps || []
            const deploys = this.$store.state.appStore?.deploymentStatus || []
            const fromDeploy = deploys.find(d => String(d.aid) === String(aid))
            const fromApp = apps.find(a => String(a.aid) === String(aid))
            const appName = (fromDeploy && fromDeploy.name) || (fromApp && (fromApp.long_id || fromApp.name)) || `app ${aid}`
            if (data.action === 'recreated') {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: `Application <strong>${appName}</strong> hit <code>${reason}</code> — retrying with a fresh container…`,
                    snackBarIcon: 'warning',
                    snackBarTimeout: 10000
                })
            } else {
                this.$store.commit('appStore/setLastStopReason', {
                    aid,
                    reason,
                    message: data.message,
                    action: 'stopped'
                })
                this.$store.dispatch('showSnackBar', {
                    snackBarText: `Application <strong>${appName}</strong> was stopped due to <code>${reason}</code>`,
                    snackBarIcon: 'error',
                    snackBarTimeout: 15000
                })
            }
            this.$store.dispatch('appStore/updateDeploymentStatus')
        })
        // Generic notifications without bespoke UI: new backend kinds need no FE deploy.
        this._unsubUserNotify = onPushEvent('user_notify', data => {
            if (!this.$auth.isAuthenticated || !data) return
            if (
                [PushEventKind.TASK_FINISHED, PushEventKind.DEPLOYMENT_STATUS_CHANGED, PushEventKind.APP_ALERT, PushEventKind.APP_AUTO_STOPPED].includes(
                    data.kind
                )
            )
                return
            const text = data.title ? `<strong>${data.title}</strong>${data.message ? ' — ' + data.message : ''}` : data.message || data.kind
            this.$store.dispatch('showSnackBar', {
                snackBarText: text,
                snackBarIcon: { error: 'error', success: 'check_circle', warning: 'warning', info: 'info' }[data.severity] || 'info',
                snackBarTimeout: 8000
            })
        })
        // Any finished task → refresh running-task badge; toast on hard failures.
        this._unsubTaskFinished = onPushEvent(PushEventKind.TASK_FINISHED, data => {
            if (!this.$auth.isAuthenticated) return
            this.$store.dispatch('userStore/fetchRunningUserTasks')
            if (!data) return
            const failed = [5, 7] // FAILED, CONTAINS_ERRORS (mixins/enums taskStatusTypes)
            if (!failed.includes(Number(data.status))) return
            let result = data.result
            if (result && typeof result === 'object') {
                try {
                    result = JSON.stringify(result)
                } catch (e) {
                    result = String(result)
                }
            }
            result = result != null && result !== '' ? String(result) : 'Task failed'
            if (result.length > 200) result = result.slice(0, 197) + '…'
            this.$store.dispatch('showSnackBar', {
                snackBarText: result,
                snackBarIcon: 'error',
                snackBarTimeout: 12000
            })
        })
        // Resync after connect/reconnect so a push gap cannot freeze the UI.
        this._unsubPushConnected = onPushConnected(() => {
            if (this.$auth.isAuthenticated) {
                this.$store.dispatch('appStore/updateDeploymentStatus')
                this.$store.dispatch('userStore/fetchRunningUserTasks')
                const iid = this.$route?.params?.iid
                if (iid) {
                    this.$store.dispatch('instanceStore/fetchSnapshots', iid)
                }
            }
        })
        // Toast only on real recovery (reconnecting → connected), never on first connect.
        let prevPushStatus = null
        this._unsubPushStatus = onPushStatus(s => {
            if (s.status === 'connected' && prevPushStatus === 'reconnecting' && this.$auth.isAuthenticated) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Live updates restored — refreshing your view.',
                    snackBarIcon: 'check_circle',
                    snackBarTimeout: 4000
                })
            }
            prevPushStatus = s.status
        })
        // Standing polls resume only when push stays down past grace — lets new FE
        // work against old BE / survive push outages. Remove with the polls once stable.
        this._pushFallbackUnsub = onPushFallback(active => {
            if (active) this.startStatusPollFallback()
            else this.stopStatusPoll()
        })

        let runs = 0
        const checkIntercomFailed = setInterval(() => {
            runs++
            if (this.$appConfig?.VUE_APP_INTERCOM_ID && window.Intercom?.failedToLoad) {
                this.intercomFailed = true
                clearInterval(checkIntercomFailed)
            }
            if (!this.frigadeFailed && window.FrigadeAssistant?.failedToLoad) {
                this.frigadeFailed = true
            }
            if (runs === 50) {
                clearInterval(checkIntercomFailed)
            }
        }, 200)
        window.addEventListener('beforeinstallprompt', e => {
            localStorage.removeItem('use-pwa-dismissed')
            e.preventDefault()
            window.deferredPrompt = e
        })
        window.addEventListener('appinstalled', () => {
            window.deferredPrompt = null
        })
        if (this.$workbox) {
            this.$workbox.addEventListener('waiting', () => {
                this.showUpdatePwaTrigger = true
            })
        }
    },
    mounted() {
        this.registerAnnouncementBanner()

        if (displayCookiePopup(this.cookiePopupInterval)) {
            setTimeout(() => {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: `<p>Nuvolos stores and uses some data about you in your browser that is required to provide full functionality.</p>`,
                    snackBarIcon: 'cookie',
                    snackBarLabel: `Understood`,
                    snackBarTimeout: 10 * 60 * 1000,
                    snackBarButtonOutline: false
                })
                localStorage.setItem('nvLastPopup', DateTime.now().toISODate())
                sessionStorage.setItem('nvPopupShown', 1)
            }, 3000)
        }
    },
    destroyed() {
        this.stopStatusPoll()
        if (this._pushFallbackUnsub) {
            this._pushFallbackUnsub()
            this._pushFallbackUnsub = null
        }
        if (this._unsubPushConnected) {
            this._unsubPushConnected()
            this._unsubPushConnected = null
        }
        if (this._unsubPushStatus) {
            this._unsubPushStatus()
            this._unsubPushStatus = null
        }
        if (this._unsubTaskFinished) {
            this._unsubTaskFinished()
            this._unsubTaskFinished = null
        }
        if (this._unsubAppAlert) {
            this._unsubAppAlert()
            this._unsubAppAlert = null
        }
        if (this._unsubUserNotify) {
            this._unsubUserNotify()
            this._unsubUserNotify = null
        }
        if (this._unsubAppAutoStopped) {
            this._unsubAppAutoStopped()
            this._unsubAppAutoStopped = null
        }
        this.unregisterAnnouncementBanner()
    },
    computed: {
        hiddenChromeRoutes() {
            return [
                'login',
                'to-login',
                'signup-index',
                'signup-home',
                'signup-options',
                'signup-provider',
                'signup-success',
                'invitation-index',
                'invitation-password',
                'invitation-home',
                'invitation-accept',
                'invitation-provider',
                'invitation-authenticated',
                'trial',
                'trial-provider'
            ]
        },
        showAppChrome() {
            if (this.$route.name === 'app-open') {
                return false
            }
            return !this.hiddenChromeRoutes.includes(this.$route.name)
        },
        routeHasPermanentSidebar() {
            return this.$route.matched.some(record => Boolean(record?.components?.['permanent-sidebar']))
        },
        isInvitationsRoute() {
            return this.$route.path.startsWith('/invitations')
        },
        showGlobalDrawer() {
            return this.$auth.isAuthenticated && !this.routeHasPermanentSidebar && !this.isInvitationsRoute
        },
        isAppOpenRoute() {
            return this.$route.name === 'app-open'
        },
        vMainStyle() {
            return this.isAppOpenRoute ? 'overflow-y: hidden' : ''
        },
        isDashboard() {
            return this.$route.name === 'home-dashboard'
        },
        showUpdatePwa() {
            if (this.forcePWAUpdate) {
                return true
            }
            if (!this.isDashboard || !this.showUpdatePwaTrigger) return
            return true
        },
        ...mapGetters('orgStore', ['isOrgManager', 'isOrgFaculty', 'isOrgMember']),
        ...mapState('orgStore', ['fetchingSpaces', 'spacesWithPrimarySnapshots']),
        ...mapState(['userOrgs', 'userInfo', 'fetchingUserInfo', 'userMetadata', 'fetchingOrgs']),
        ...mapState('spaceStore', ['spaceInstances', 'fetchingInstances']),
        ...mapState('snapshotStore', ['applications']),
        ...mapState(['snackBarText', 'snackBarIcon', 'snackBarLabel']),
        ...mapGetters('spaceStore', ['isSpaceAdmin', 'currentSpaceType']),
        ...mapGetters('instanceStore', ['isInstanceEditor', 'isInstanceViewer', 'isInstanceObserver']),
        areFrigadeRolesReady() {
            if (this.fetchingUserInfo || this.fetchingOrgs) return false

            if (!this.userInfo || !this.userOrgs?.length) return false

            if (this.$route.params.oid) {
                const hasCurrentOrg = this.userOrgs.some(org => org.oid.toString() === this.$route.params.oid.toString())
                if (!hasCurrentOrg) return false
            }

            if (this.$route.params.sid) {
                if (this.fetchingSpaces) return false
                const hasCurrentSpace =
                    Array.isArray(this.spacesWithPrimarySnapshots) &&
                    this.spacesWithPrimarySnapshots.some(space => space.sid.toString() === this.$route.params.sid.toString())
                if (!hasCurrentSpace) return false
            }

            if (this.$route.params.iid) {
                if (this.fetchingInstances) return false
                const hasCurrentInstance =
                    Array.isArray(this.spaceInstances) && this.spaceInstances.some(i => i.iid.toString() === this.$route.params.iid.toString())
                if (!hasCurrentInstance) return false
            }

            return true
        },
        parseCurrentUrl() {
            if (window.location.href.length) {
                return parseURL(window.location.href)
            }
            return null
        },
        fetchingSpaceInstancePrerequisiteLoaded() {
            if (this.$route.params.sid && !this.fetchingSpaces) {
                return this.$route.params.sid
            } else {
                return false
            }
        }
    },
    watch: {
        fetchingSpaceInstancePrerequisiteLoaded: {
            handler() {
                this.$store.dispatch('spaceStore/fetchSpaceInstances', this.$route.params.sid)
            },
            immediate: true
        },
        '$auth.isAuthenticated': {
            handler(to) {
                if (to) {
                    this.$store.dispatch('getUserInfo')
                } else {
                    disconnectPushSocket()
                }
            },
            immediate: true
        },
        $route(to, from) {
            if (to.name.includes('reporting')) return
            if (to.params.snid === undefined && from !== undefined && from.params.snid !== undefined) {
                this.$store.dispatch('snapshotStore/resetSnapshotState')
            }
            if (
                (to.params.iid && from.params.iid && to.params.iid.toString() !== from.params.iid.toString()) ||
                (from.params.iid && to.params.iid === undefined)
            ) {
                this.$store.dispatch('instanceStore/resetInstanceState')
            }
            if (
                (to.params.sid && from.params.sid && to.params.sid.toString() !== from.params.sid.toString()) ||
                (from.params.sid && to.params.sid === undefined)
            ) {
                this.$store.dispatch('spaceStore/resetSpaceState')
            }
            if (to.name === 'home-dashboard') {
                this.$store.dispatch('appStore/fetchRecentApps', this.$route.params.oid)
            }
            if (to.name === 'user-ssh-keys') {
                this.$store.dispatch('userStore/fetchSSHKeys')
            }
            if (
                (to.params.iid && from.params.iid && to.params.iid.toString() !== from.params.iid.toString()) ||
                (to.params.iid && from.params.iid === undefined && to.params.iid !== from.params.iid)
            ) {
                this.$store.dispatch('instanceStore/fetchInstanceSnapshots', to.params.iid)
                this.$store.dispatch('appStore/fetchAllUserApps', { iid: to.params.iid, startFirstApp: false })
                this.$store.dispatch('instanceStore/setSnapshotPollingInterval', to.params.iid)
            }
            if (
                (to.params.sid && from.params.sid && to.params.sid.toString() !== from.params.sid.toString()) ||
                (to.params.sid && from.params.sid === undefined && to.params.sid !== from.params.sid)
            ) {
                this.$store.dispatch('spaceStore/fetchSpaceBundles', to.params.sid)
                this.$store.dispatch('spaceStore/fetchMappedResources', to.params.sid)
                this.$store.dispatch('spaceStore/fetchCurrentSpace')
            }
            if (
                (to.params.oid && from.params.oid && to.params.oid.toString() !== from.params.oid.toString()) ||
                (to.params.oid && from.params.oid === undefined)
            ) {
                const orgData = this.userOrgs.filter(org => org.oid.toString() === to.params.oid.toString())[0]
                this.$store.dispatch('setCurrentOrgData', orgData)
                this.$store.dispatch('orgStore/fetchOrgSpaces', to.params.oid)
                this.$store.dispatch('orgStore/fetchOrgTokens', to.params.oid)
            }
            // keep intercom launcher hidden after it has been opened explicitly
            if (window.Intercom && this.$appConfig?.VUE_APP_INTERCOM_ID) {
                this.intercomUpdate()
            }
            if (to.params.oid) {
                this.$store.dispatch('userStore/fetchDefaultResourcePool', to.params.oid)
            }
        },
        isSpaceAdmin: {
            handler: function (to, from) {
                if (to) {
                    this.intercomUpdate({
                        is_space_admin: true
                    })
                }
            },
            immediate: true
        },
        currentSpaceType: {
            handler: function (to, from) {
                if (to) {
                    if (to === this.spaceTypes.RESEARCH_SPACE) {
                        this.intercomUpdate({
                            has_research_space: true
                        })
                    }
                    if (to === this.spaceTypes.EDUCATION_SPACE) {
                        this.intercomUpdate({
                            has_edu_space: true
                        })
                    }
                }
            },
            immediate: true
        },
        userInfo: {
            handler(to, from) {
                if (to) {
                    this.$store.dispatch('appStore/updateDeploymentStatus')
                    this.$store.dispatch('fetchUserOrgs')
                    this.$store.dispatch('userStore/fetchAvailableResourcePoolData')

                    connectPushSocket()

                    // Push is primary. Standing polls are owned by onPushFallback
                    // (created above) — they arm only when push stays down past grace.
                }
            },
            immediate: true
        },
        fetchingUserInfo(fetching) {
            if (!fetching && this.userMetadata?.new_user) {
                const date = new Date()
                date.setDate(date.getDate() + 1)
                const usermeta = {
                    show_install_pwa: date.toISOString(),
                    analytics_opt_in: true
                }
                this.$store.commit('setUserMetadata', usermeta)
            }
        },
        areFrigadeRolesReady: {
            handler(ready) {
                if (ready) {
                    this.tryBootFrigade(this.userInfo)
                }
            },
            immediate: true
        },
        showAnnouncementBanner(show) {
            if (show) {
                this.registerAnnouncementBanner()
            } else {
                this.unregisterAnnouncementBanner()
            }
        }
    },
    errorCaptured(error, vm, info) {
        const chunkFailedMessage = /Loading chunk [\d]+ failed/
        if (error?.message && chunkFailedMessage.test(error.message)) {
            this.forcePWAUpdate = true
        }
        return false
    }
}
</script>

<style lang="scss">
@import '@/sass/nuvolos.scss';
</style>

<style scoped>
.intercom-padding {
    padding-bottom: 98px !important;
}

.announcement-banner {
    border-radius: 0 0 0 0 !important;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 99999;
    margin-bottom: 0;
}
</style>
